import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';

import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucidePlus,
  lucideList,
  lucideFileText,
  lucideSettings,
  lucidePhone,
  lucideRefreshCw,
  lucideSend,
  lucideClock,
  lucideTrendingUp,
  lucideChevronRight,
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { HlmButton } from '@spartan-ng/helm/button';

import { PageHeaderComponent, StatusBadgeComponent } from '../../../shared/ui/components';

interface Inquiry {
  id: number;
  inquiryNumber: string;
  clientName: string;
  origin: string;
  destination: string;
  status: 'New' | 'In Progress' | 'Quoted' | 'Proceed' | 'Rejected';
}

interface Quote {
  id: number;
  quoteNumber: string;
  clientName: string;
  sentDate: Date;
  isOverdue: boolean;
}

@Component({
  selector: 'app-forwarding-dashboard',
  standalone: true,
  // Default CD kept intentionally: component uses plain mutable properties.
  imports: [
    DatePipe,
    RouterModule,
    NgIcon,
    HlmIcon,
    HlmButton,
    PageHeaderComponent,
    StatusBadgeComponent,
  ],
  providers: [
    DatePipe,
    provideIcons({
      lucidePlus,
      lucideList,
      lucideFileText,
      lucideSettings,
      lucidePhone,
      lucideRefreshCw,
      lucideSend,
      lucideClock,
      lucideTrendingUp,
      lucideChevronRight,
    }),
  ],
  templateUrl: './forwarding-dashboard.html',
  styleUrl: './forwarding-dashboard.css',
})
export class ForwardingDashboardComponent implements OnInit {
  // Metrics
  pendingInquiries = 8;
  quotesSent = 24;
  awaitingReply = 12;
  overdueFollowups = 3;
  conversionRate = 68;

  recentInquiries: Inquiry[] = [
    {
      id: 1,
      inquiryNumber: 'HT-INQ-2025-00001',
      clientName: 'ABC Trading Co.',
      origin: 'SHA',
      destination: 'MNL',
      status: 'New',
    },
    {
      id: 2,
      inquiryNumber: 'ATR-INQ-2025-00002',
      clientName: 'Global Freight Ltd.',
      origin: 'HKG',
      destination: 'CEB',
      status: 'In Progress',
    },
    {
      id: 3,
      inquiryNumber: 'HT-INQ-2025-00003',
      clientName: 'Express Logistics',
      origin: 'SGN',
      destination: 'MNL',
      status: 'Quoted',
    },
  ];

  quotesForFollowup: Quote[] = [
    {
      id: 1,
      quoteNumber: 'QT-2025-00012',
      clientName: 'ABC Trading Co.',
      sentDate: new Date('2025-01-18'),
      isOverdue: true,
    },
    {
      id: 2,
      quoteNumber: 'QT-2025-00013',
      clientName: 'Express Logistics',
      sentDate: new Date('2025-01-20'),
      isOverdue: false,
    },
  ];

  ngOnInit(): void {
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    // TODO: Implement API calls to load real data
  }

  getStatusVariant(status: Inquiry['status']): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default' {
    const map: Record<Inquiry['status'], 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'default'> = {
      'New': 'default',
      'In Progress': 'info',
      'Quoted': 'success',
      'Proceed': 'success',
      'Rejected': 'danger',
    };
    return map[status] ?? 'neutral';
  }

  followUp(quoteId: number): void {
    // TODO: Implement follow-up logic (open modal, send email, etc.)
    void quoteId;
  }
}
